import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Box, CircularProgress, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Dialog, DialogTitle, DialogContent } from "@mui/material";



const PublicProofPages: React.FC = () => {
    const { exportId, username } = useParams<{ exportId: string; username: string }>();

    const realExportId = exportId?.includes("_")
        ? exportId.split("_").pop()
        : exportId;
   
    type PageStatus = {
        status: "pending" | "approved" | "rejected";
        comment: string;
    };
  
    type Page = {
        src: string;
        pageIndex: number; //  real album index (zero-based or one-based, your choice)
    };

    const [albumInfo, setAlbumInfo] = useState<{
        name: string;
        albumSize: {
            width: number;
            height: number;
            unit: string;
        };
        pageCount: number;
        clientName: string;
    } | null>(null);


    const [pages, setPages] = useState<Page[]>([]);
    const [pageStatuses, setPageStatuses] = useState<Record<number, PageStatus>>({});
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [submitted, setSubmitted] = React.useState(false);
    const [buttonDisabled, setButtonDisabled] = useState(false);

    const [reviewSubmitted, setReviewSubmitted] = useState(false);

    const [currentSpread, setCurrentSpread] = useState(0);
    const activePage = pages[currentSpread];
    const [showComments, setShowComments] = useState(false);
    const [previousReview, setPreviousReview] = useState<
        {
            pageIndex: number;
            status: "pending" | "approved" | "rejected";
            comment: string;
            photographerNote?: string;
        }[]
    >([]);

 
    const previousComment = previousReview?.find(
        r =>
            r.pageIndex === activePage?.pageIndex &&
            (
                (r.comment?.trim() ?? "") !== "" ||
                (r.photographerNote?.trim() ?? "") !== ""
            )
    );

    useEffect(() => {
        if (!reviewSubmitted) return;

        const timer = setTimeout(() => {
            window.close();

            // Fallback
            window.location.replace("about:blank");
        }, 4000);

        return () => clearTimeout(timer);
    }, [reviewSubmitted]);




   
    useEffect(() => {  
        if (!exportId || !username) {
     
            return;
        }
        const fetchPages = async () => {
            try {
                setLoading(true);

                const res = await fetch(`/eda_online/api/export/web-proof/images/${username}/${realExportId}`);
                if (!res.ok) {
                    throw new Error(`Failed to load proof pages: ${res.statusText}`);
                }
                const data: string[] = await res.json();
                const pagesWithIndex: Page[] = data.map((file: string) => {
                    const segments = file.split("/");
                    const encodedFilename = segments[segments.length - 1];
                    const filename = decodeURIComponent(encodedFilename);
                    // Extract number from: Spread_9.jpg
                    const match = filename.match(/_(\d+)\.jpg/i);
                    const pageNumber = match ? parseInt(match[1], 10) : 1;

                    return {
                        src: file,        // <--- use backend path directly
                        pageIndex: pageNumber - 1
                    };
                });

                setPages(pagesWithIndex);

            } catch (err) {
                console.error(err);
                setError("The album was not found");
            } finally {
                setLoading(false);
            }
        };

        fetchPages();
    }, [realExportId, username]);


    useEffect(() => {
        if (!exportId || !username) return;

        const fetchAlbumInfo = async () => {
            try {
                const res = await fetch(`/eda_online/api/export/web-proof/${username}/${realExportId}`);
                if (!res.ok) throw new Error("Failed to fetch album info");
                const data = await res.json();


                setAlbumInfo({
                    name: data.albumName,
                    albumSize: data.albumSize,
                    pageCount: data.exportedPages ?? 0,
                    clientName:
                        data.clients?.groomName ||
                        data.clients?.brideName ||
                        "",
                });

                if (data.review?.length) {
                    setPreviousReview(data.review);
                }


            } catch (err) {
                console.error(err);
            }
        };

        fetchAlbumInfo();
    }, [realExportId, username]);


    const handleCommentChange = (index: number, value: string) => {
        setPageStatuses(prev => ({
            ...prev,
            [index]: {
                status: prev[index]?.status ?? "pending",
                comment: value
            }
        }));
    };

  

    const handleSubmitReview = async () => {

        if (!realExportId || !username || submitted) {
            return;
        }

        try {
            setSubmitted(true);

            const payload = pages.map((page) => {
                const review = pageStatuses[page.pageIndex];

                const comment = review?.comment?.trim() || "";

                return {
                    pageIndex: page.pageIndex,
                    status: comment.length > 0 ? "rejected" : "approved",
                    comment: comment
                };
            });
        

            const res = await fetch(
                `/eda_online/api/export/web-proof/review/${username}/${realExportId}`,
                {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ pages: payload }),
                }
            );



            if (!res.ok) {
                const text = await res.text();
                console.error("Backend error:", text);
                throw new Error("Failed to save review");
            }



            setButtonDisabled(true);
            setReviewSubmitted(true);

        } catch (err) {
            console.error("Submit review failed:", err);
            setSubmitted(false);
        }
    };



    /* ---------------- LOADING ---------------- */
    if (loading) {
        return (
            <Box
                sx={{
                    height: "100vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "#111",
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    /* ---------------- ERROR ---------------- */
    if (error) {
        return (
            <Box
                sx={{
                    height: "100vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "#111",
                    color: "#fff",
                }}
            >
                <Typography variant="h6">{error}</Typography>
            </Box>
        );
    }

    /* ---------------- EMPTY ---------------- */
    if (!pages.length) {
        return (
            <Box
                sx={{
                    height: "100vh",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "#111",
                    color: "#fff",
                }}
            >
                <Typography variant="h6">There are no pages to preview.</Typography>
            </Box>
        );
    }

    /* ---------------- MAIN VIEW ---------------- */

    if (reviewSubmitted) {
        return (
            <Box
                sx={{
                    height: "100vh",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    backgroundColor: "#d7d6d6",
                }}
            >
                <Box
                    sx={{
                        width: 740,
                        height: 380,
                        backgroundColor: "#5f5f5f",
                        borderRadius: 3,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        alignItems: "center",
                        textAlign: "center",
                        boxShadow: "0 8px 25px rgba(0,0,0,0.25)",
                    }}
                >
                    <CheckCircleIcon
                        sx={{
                            fontSize: 70,
                            color: "#6eafd7",
                            mb: 2,
                        }}
                    />

                    <Typography
                        sx={{
                            fontSize: 22,
                            fontWeight: 600,
                            color: "#fff",
                        }}
                    >
                        Thank you! Your review has been successfully recorded.
                    </Typography>

                    <Typography
                        sx={{
                            mt: 1,
                            fontSize: 15,
                            color: "#ddd",
                        }}
                    >
                        Your feedback has been sent to the photographer.
                    </Typography>

                    <Typography
                        sx={{
                            mt: 3,
                            fontSize: 14,
                            color: "#aaa",
                        }}
                    >
                        This page will close automatically.
                    </Typography>
                </Box>
            </Box>
        );
    }


    const hasComments = Object.values(pageStatuses)
        .some(p => p.comment && p.comment.trim().length > 0);

    const commentCount = Object.values(pageStatuses)
        .filter(p => p.comment && p.comment.trim().length > 0)
        .length;

 

    const spreadContainer = {
        width: "100%",
        maxWidth: "1800px",
        margin: "0 auto",
    };

    const albumPadding = 30;

    // Your album is always open, so width is spread width x 2
    const albumAspectRatio = (() => {
        if (!albumInfo?.albumSize?.width || !albumInfo?.albumSize?.height) {
            return null;
        }

        const { width, height } = albumInfo.albumSize;

        return (width * 2) / height;
    })();
  
    // Example: 60x30 album
   const availableWidth = window.innerWidth * 0.9;
   const availableHeight = window.innerHeight - 120;

const albumHeight = albumAspectRatio
    ? Math.min(
        availableHeight,
        availableWidth / albumAspectRatio
    )
    : 0;

const albumWidth = albumAspectRatio
    ? albumHeight * albumAspectRatio
    : 0;

 
   
   return (
        <Box
            sx={{
                height: "100vh",
                overflow: "hidden",
                backgroundColor: "#d0d0d0",
                display: "flex",
                flexDirection: "column",
            }}
        >

            {/* CENTERED ALBUM AREA */}
           <Box
               sx={{
                   flex: 1,
                   display: "flex",
                   justifyContent: "center",
                   alignItems: "center",
                   overflow: "hidden",
               }}
           >
               <Box
                   sx={{
                       ...spreadContainer,
                       height: "100%",
                       display: "flex",
                       justifyContent: "center",
                       alignItems: "center",
                   }}
               >
                   <Box
                       sx={{
                           width: albumWidth,
                           height: "100%",
                           padding: `${albumPadding}px`,
                           boxSizing: "border-box",
                           display: "flex",
                           flexDirection: "column",
                       }}
                   >

                    {/* TOP BAR */}
                       <Box
                           sx={{
                               height: 70,
                               width: `calc(100% - ${albumPadding * 2}px)`,
                               margin: "0 auto",
                               display: "flex",
                               alignItems: "center",
                               justifyContent: "space-between",
                               position: "relative",
                               boxSizing: "border-box",
                           }}
                       >

                        {/* KEEP YOUR BUTTONS HERE */}
                           <Box
                               sx={{
                                   display: "flex",
                                   gap: 1,
                                   flexShrink: 0,
                               }}
                           >
                           <button
                                disabled={hasComments}
                                onClick={handleSubmitReview}
                                style={{
                                     backgroundColor: !hasComments ? "#6eafd7" : "#999",
                                     color: "#fff",
                                     border: "none",
                                     borderRadius: 4,
                                     height: "42px",
                                     padding: "0 18px",
                                     cursor: "pointer",
                                   }}
                               >
                                   Approve Album
                               </button>

                           <button
                               disabled={!hasComments}
                               onClick={handleSubmitReview}
                               style={{
                                   backgroundColor: hasComments ? "#6eafd7" : "#999",
                                   color: "#fff",
                                   border: "none",
                                   borderRadius: 4,
                                   height: "42px",
                                   padding: "0 18px",
                                   cursor: hasComments ? "pointer" : "not-allowed",
                               }}
                           >
                               Submit Feedback
                           </button>
                       </Box>

                           <Typography
                               sx={{
                                   position: "absolute",
                                   left: "50%",
                                   transform: "translateX(-50%)",
                                   fontWeight: 600,
                                   whiteSpace: "nowrap",
                               }}
                           >
                               {albumInfo?.name || "Album"}
                           </Typography>

                           <Box
                               sx={{
                                   display: "flex",
                                   alignItems: "center",
                                   gap: 1,
                                   flexShrink: 0,
                               }}
                           >
                           <Typography
                               sx={{
                                   fontWeight: 600,
                                   color: "#888"
                               }}
                           >
                               {albumInfo?.clientName || "Client"}
                           </Typography>

                           <img
                               src={`${process.env.PUBLIC_URL}/client-icon.png`}
                               alt="Client"
                               style={{
                                   width: 26,
                                   height: 26,
                                   opacity: 0.6,
                               }}
                           />
                       </Box>
                    </Box>


                    {/* VIEWER */}
                       <Box
                           sx={{
                               flex: 1,
                               width: `calc(100% - ${albumPadding * 2}px)`,
                               margin: "0 auto",
                               position: "relative",
                               display: "flex",
                               justifyContent: "center",
                               alignItems: "center",
                               minHeight: 0,
                           }}
                       >

                       {previousComment && (
                           <Box
                               sx={{
                                   position: "absolute",
                                   bottom: -50,
                                   left: "50%",
                                   transform: "translateX(-50%)",
                                   background: "rgba(255,255,255,0.95)",
                                   borderLeft: "4px solid #6eafd7",
                                   padding: "10px 15px",
                                   borderRadius: 1,
                                   maxWidth: "70%",
                                   boxShadow: 2,
                                   zIndex: 5,
                               }}
                           >
                               <Typography
                                   sx={{
                                       fontSize: 12,
                                       fontWeight: 600,
                                       color: "#555",
                                   }}
                               >
                                   Previous feedback
                               </Typography>

                               <Typography
                                   sx={{
                                       fontSize: 13,
                                       color: "#333",
                                       fontStyle: "italic",
                                   }}
                               >
                                   "{previousComment.comment}"
                               </Typography>


                               {previousComment.photographerNote && (
                                   <>
                                       <Typography
                                           sx={{
                                               mt: 1,
                                               fontSize: 12,
                                               fontWeight: 600,
                                               color: "#6eafd7",
                                           }}
                                       >
                                           Review conversation
                                       </Typography>

                                       <Typography
                                           sx={{
                                               fontSize: 13,
                                               color: "#333",
                                           }}
                                       >
                                           {previousComment.photographerNote}
                                       </Typography>
                                   </>
                               )}
                           </Box>
                       )}

                      
                       {/* LEFT ARROW */}
                       {currentSpread > 0 && (
                           <button
                               onClick={() =>
                                   setCurrentSpread(prev => Math.max(prev - 1, 0))
                               }
                               style={{
                                   position: "absolute",
                                   left: -40,
                                   top: "50%",
                                   transform: "translateY(-50%)",
                                   fontSize: 50,
                                   border: "none",
                                   background: "transparent",
                                   color: "#777",
                                   cursor: "pointer",
                               }}
                           >
                               ‹
                           </button>
                       )}

                        {activePage && (
                               <img
                                   src={activePage.src}
                                   alt="Current spread"
                                   style={{
                                       width: "100%",
                                       height: "100%",
                                       objectFit: "contain",
                                   }}
                               />
                        )}

                       {/* RIGHT ARROW */}
                       {currentSpread < pages.length - 1 && (
                           <button
                               onClick={() =>
                                   setCurrentSpread(prev =>
                                       Math.min(prev + 1, pages.length - 1)
                                   )
                               }
                               style={{
                                   position: "absolute",
                                   right: -40,
                                   top: "50%",
                                   transform: "translateY(-50%)",
                                   fontSize: 50,
                                   border: "none",
                                   background: "transparent",
                                   color: "#777",
                                   cursor: "pointer",
                               }}
                           >
                               ›
                           </button>
                       )}
                   </Box>
                       {/* PAGE NUMBER */}
                       <Box
                           sx={{
                               height: 30,
                               width: `calc(100% - ${albumPadding * 2}px)`,
                               margin: "0 auto",
                               display: "flex",
                               justifyContent: "flex-end",
                               alignItems: "center",
                               boxSizing: "border-box",
                           }}
                       >
                           <Typography
                               sx={{
                                   color: "#555",
                                   fontSize: 13,
                               }}
                           >
                               Spread {currentSpread + 1} / {pages.length}
                           </Typography>
                       </Box>
                </Box>
            </Box>

           </Box>


            {/* COMMENT BAR */}
            <Box
                sx={{
                   backgroundColor: "#8b8b8b",
                    padding: "10px 20px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                }}
            >
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        width: "60%",
                    }}
                >

                    {/* COMMENT COUNT */}
                   <Box
                       onClick={() => setShowComments(true)}
                       sx={{
                           display: "flex",
                           alignItems: "center",
                           gap: 0.5,
                           cursor: "pointer",
                           "&:hover": {
                               opacity: 0.8,
                           },
                       }}
                   >
                       <img
                           src={`${process.env.PUBLIC_URL}/comments_icon.png`}
                           alt="Comments"
                           style={{
                               width: 42,
                               height: 35,
                               marginTop:1
                           }}
                       />

                       <Typography
                           sx={{
                               color: "#fff",
                               fontSize: 14,
                               whiteSpace: "nowrap",
                               minWidth: 80,
                           }}
                       >
                           {commentCount} {commentCount === 1 ? "comment" : "comments"}
                       </Typography>
                   </Box>

                 

                    {/* COMMENT INPUT */}
                   <textarea
                       className="comment-textarea"
                       value={pageStatuses[activePage?.pageIndex]?.comment || ""}
                       onChange={(e) =>
                           activePage &&
                           handleCommentChange(
                               activePage.pageIndex,
                               e.target.value
                           )
                       }
                       placeholder="Start typing here to add a new comment"
                   
                       style={{
                           width: "50vw",
                           height: "35px",
                           background: "#fff",
                           color: "#222",
                           borderRadius: 4,
                           fontSize: 13,
                           resize: "none",
                           boxSizing: "border-box",
                       }}
                    />

                </Box>
           </Box>


           <Dialog
               open={showComments}
               onClose={() => setShowComments(false)}
               maxWidth="sm"
               fullWidth
           >
               <DialogTitle>
                   Album Comments
               </DialogTitle>

               <DialogContent>

                   {Object.entries(pageStatuses)
                       .filter(([_, value]) => value.comment.trim() !== "")
                       .map(([pageIndex, value]) => (
                           <Box
                               key={pageIndex}
                               sx={{
                                   mb: 2,
                                   padding: 1.5,
                                   background: "#f5f5f5",
                                   borderRadius: 1,
                                   border: "1px solid #c9c9c9",
                               }}
                           >
                               <Typography
                                   sx={{
                                       fontWeight: 600,
                                       color: "#6eafd7",
                                       mb: 0.5,
                                   }}
                               >
                                   Spread {Number(pageIndex) + 1}/{pages.length}
                               </Typography>

                               <Typography
                                   sx={{
                                       color: "#333",
                                   }}
                               >
                                   {value.comment}
                               </Typography>
                           </Box>
                       ))}

               </DialogContent>
           </Dialog>
          

        </Box>
    );


};

export default PublicProofPages;